import { fn } from '@ember/helper';
import { on } from '@ember/modifier/on';
import { action } from '@ember/object';
import { service } from '@ember/service';

import type { DSSetup, PopupData, Service } from 'webapp-types';
import { BillingFeature } from 'webapp/enums/enums';
import { NotificationCreateType } from 'webapp/enums/notifications';

import safeStyleHelper from 'webapp/helpers/safe-style';
import BoardsPopupSelectListSingleComponent from 'webapp/components/boards/popup-select-list-single';
import CommonUiIcon from 'webapp/components/common/ui-icon';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';

import type { BoardSelectListSingleItem } from 'webapp/models-custom/filter-sort/board-select-list-single';
import MetricSetup from 'webapp/models-custom/metric-setup';
import Report from 'webapp/models-custom/report';

const enum ChoiceType {
	METRIC = 'metric',
	BOARD = 'board',
	REPORT = 'report',
}

export default class NotificationsPopupCreateChoiceComponent extends PopupInnerModalComponent<PopupData.Notification.CreateChoice> {
	@service('model-manager/notifications')
	declare mmNotificationsService: Service.ModelManager.Notifications;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('billing') declare billingService: Service.Billing;
	@service('popup') declare popupService: Service.Popup;

	options: {
		type: ChoiceType;
		icon: IconName;
		color: string;
		title: string;
		description: string;
	}[] = [
		{
			type: ChoiceType.METRIC,
			icon: 'Metrics',
			color: 'var(--databox-orange-500)',
			title: 'Metric',
			description: 'Monitor key metrics with alerts or recurring updates',
		},
		{
			type: ChoiceType.BOARD,
			icon: 'Databoards',
			color: 'var(--databox-brand-500)',
			title: 'Dashboard',
			description: 'Send scheduled snapshots of a dashboard or looped dashboards',
		},
		{
			type: ChoiceType.REPORT,
			icon: 'Reports',
			color: 'var(--databox-violet-500)',
			title: 'Report',
			description: 'Send reports on a schedule or set review reminders',
		},
	];

	get origin() {
		return this.popupData.origin;
	}

	@action
	select(type: ChoiceType) {
		if (
			type === ChoiceType.REPORT &&
			!this.billingService.checkIfCan(BillingFeature.REPORTS_BASIC, true)
		) {
			return;
		}

		if (type === ChoiceType.METRIC) {
			this.openMetricSelectPopup();
		} else {
			this.openSelectSinglePopup(type === ChoiceType.REPORT);
		}
	}

	private openMetricSelectPopup() {
		void this.mmMetricsService.openMetricSelectPopup({
			onChange: (metricData: DSSetup.MetricData) => {
				// create digest notification
				const metricSetup = new MetricSetup(metricData);
				this.mmNotificationsService.createNewForFeature(
					NotificationCreateType.METRIC,
					metricSetup,
					this.origin,
					true,
				);
				this.close();
			},
		});
	}

	private openSelectSinglePopup(isReport = false) {
		const popup = this.popupService.createModal(BoardsPopupSelectListSingleComponent, {
			popupClass: 'popup-board-select',
		});
		popup.open({
			onConfirm: (item: BoardSelectListSingleItem) => {
				const createType =
					item instanceof Report
						? NotificationCreateType.REPORT
						: NotificationCreateType.BOARD;
				this.mmNotificationsService.createNewForFeature(
					createType,
					item,
					this.origin,
					true,
				);
				this.close();
			},
			isReport,
		});
	}

	<template>
		<div class="title">
			<CommonUiIcon @name="Bell" @size="lg" />
			What would you like to be notified about?
		</div>

		<div class="content">

			<ul>
				{{#each this.options as |option|}}
					<li class="item">
						<a {{on "click" (fn this.select option.type)}}>
							<div
								class="main-icon"
								style={{safeStyleHelper option.color property="color"}}
							>
								<CommonUiIcon @name={{option.icon}} @size="xl" />
							</div>
							<div class="text">
								<strong>{{option.title}}</strong>
								<p>{{option.description}}</p>
							</div>
							<CommonUiIcon @name="ArrowRight" @size="md" />
						</a>
					</li>
				{{/each}}
			</ul>

		</div>
	</template>
}
